import React, { useEffect, useState } from "react";
import Modal from "../../components/UI/Modal";
import Button from "../../components/UI/Button";
import ConfirmDialog from "../../components/UI/ConfirmDialog";
import { partnersApi, uploadApi } from "../../api/client";
import toast from "react-hot-toast";
import { Edit2, Trash2, Plus } from "lucide-react";

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // form state
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePublicId, setImagePublicId] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await partnersApi.list();
      // API returns { partners: [...], pagination: {...} }
      const payload = res.data;
      setPartners(Array.isArray(payload) ? payload : payload?.partners || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setTitle("");
    setText("");
    setImageUrl("");
    setImagePublicId("");
    setIsOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p._id);
    setTitle(p.title || "");
    setText(p.text || "");
    setImageUrl(p.imageUrl || "");
    setImagePublicId(p.imagePublicId || "");
    setIsOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      setUploading(true);
      const res = await uploadApi.single(form);
      const { url, publicId } = res.data;
      setImageUrl(url);
      setImagePublicId(publicId || "");
      toast.success("Image uploaded");
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");

    const payload = { title, text, imageUrl, imagePublicId };
    try {
      if (editing) {
        await partnersApi.update(editing, payload);
        toast.success("Partner updated");
      } else {
        await partnersApi.create(payload);
        toast.success("Partner created");
      }
      setIsOpen(false);
      fetchPartners();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save partner");
    }
  };

  const handleDelete = async (id) => {
    // open confirm dialog
    setDeletingId(id);
    setShowDelete(true);
  };

  const [showDelete, setShowDelete] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      setDeleting(true);
      await partnersApi.delete(deletingId);
      toast.success("Partner deleted");
      fetchPartners();
      setShowDelete(false);
      setDeletingId(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete partner");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Partners
        </h2>
        <Button variant="primary" onClick={openAdd} icon={Plus}>
          Add Partner
        </Button>
      </div>

      <div>
        {loading ? (
          <div className="text-center py-8">Loading partners...</div>
        ) : partners.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No partners found.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.map((p) => (
              <div
                key={p._id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col"
              >
                {p.imageUrl && (
                  <img
                    src={p.imageUrl}
                    alt={p.title}
                    className="h-40 w-full object-cover rounded-md mb-3"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {p.text}
                  </p>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openEdit(p)}
                    icon={Edit2}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(p._id)}
                    icon={Trash2}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? "Edit Partner" : "Add Partner"}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Text
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Image
            </label>
            <div className="mt-2 flex items-center gap-4">
              <input type="file" accept="image/*" onChange={handleFileChange} />
              {uploading && (
                <div className="text-sm text-gray-500">Uploading...</div>
              )}
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                alt="preview"
                className="mt-3 h-32 object-cover rounded-md"
              />
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={uploading}>
              {editing ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => {
          setShowDelete(false);
          setDeletingId(null);
        }}
        title="Delete Partner"
        description={
          partners.find((p) => p._id === deletingId)
            ? `Are you sure you want to delete "${partners.find((p) => p._id === deletingId).title}"? This action cannot be undone.`
            : "Are you sure you want to delete this partner? This action cannot be undone."
        }
        confirmLabel="Delete Partner"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default PartnersManager;
